import React, { useRef, useEffect, useMemo } from "react";
import { Container, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { closeMenu } from "../../../../State/actions";
import { useDispatch } from "react-redux";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";

// Tap padding around the X in parent/display space (not texture-local).
// Mobile gets a larger margin — finger taps drift more than a mouse.
const HIT_PAD_FRACTION = 0.5;
const HIT_PAD_FRACTION_MOBILE = 1.0;

// TEMP DEBUG: translucent red box over the real tappable area.
// Set to false once the close-button hit issue is confirmed fixed.
const DEBUG_HIT_AREA = false;

const ButtonMenuClose = ({
  onClose,
  x = 0,
  y = 0,
  isSettings = false,
  sizeMultiplier = 1.0,
}) => {
  const { t } = useTranslation();
  const { getResponsiveConfig, device } = useDimensions();
  const dispatch = useDispatch();

  // Prevent double-fire while close animation / press is in flight
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness();

  const texture = getNewUITexture("button_x");

  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);
  const buttonWidth = buttonConfig.width * sizeMultiplier;
  const buttonHeight = buttonConfig.height * sizeMultiplier;

  // Hit rect in CONTAINER local space (display pixels). Must NOT live on the
  // Sprite — Sprite width/height sets scale, and Pixi tests hitArea in
  // texture-local space, so a display-pixel rect on the sprite misses most taps.
  // Same pattern as ButtonSound (Graphics hit target + non-interactive sprite).
  const hitArea = useMemo(() => {
    const isMobile =
      device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.MOBILE_SMALL;
    const padFraction = isMobile ? HIT_PAD_FRACTION_MOBILE : HIT_PAD_FRACTION;
    const pad = Math.round(Math.min(buttonWidth, buttonHeight) * padFraction);
    return new PIXI.Rectangle(
      -buttonWidth / 2 - pad,
      -buttonHeight / 2 - pad,
      buttonWidth + pad * 2,
      buttonHeight + pad * 2,
    );
  }, [buttonWidth, buttonHeight, device]);

  const handleClick = () => {
    if (isProcessingClickRef.current) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    if (buttonRef.current) {
      triggerPress();
    }

    if (onClose) {
      onClose();
      dispatch(closeMenu());
    }

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      cleanupFilters();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [cleanupFilters]);

  return (
    <Container name="secondary_button_menu_close" x={x} y={y}>
      {DEBUG_HIT_AREA && (
        <Graphics
          eventMode="none"
          draw={(g) => {
            g.clear();
            g.beginFill(0xff0000, 0.25);
            g.drawRect(hitArea.x, hitArea.y, hitArea.width, hitArea.height);
            g.endFill();
          }}
        />
      )}

      {/* Interactive hit target — unscaled container space (matches red debug box) */}
      <Graphics
        name="secondary_button_menu_close_hit"
        eventMode="static"
        cursor="pointer"
        hitArea={hitArea}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        accessibleTitle={t("close")}
        accessibleHint={t("close")}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill required for hit detection
          g.beginFill(0x000000, 0.01);
          g.drawRect(hitArea.x, hitArea.y, hitArea.width, hitArea.height);
          g.endFill();
        }}
      />

      {/* Visual only — press/hover animate this sprite, never the hit target */}
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="secondary_button_menu_close_icon"
        anchor={0.5}
        width={buttonWidth}
        height={buttonHeight}
        x={0}
        y={0}
        eventMode="none"
        roundPixels={true}
      />
    </Container>
  );
};

export default ButtonMenuClose;
